import React, { useRef, useState } from "react";
import { Typography, Switch, Card, Button, Row, Col, Tag, Divider, Slider } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import "./Plans.scss";
import pricingData from "../../data/pricingData.json";
import * as ENVIRONMENT from '../../../../env.js';
import { trackCta } from '../../../../analytics';

const { Paragraph } = Typography;

const DynamicFeaturesTable = ({ tableData, planDefinitions }) => {
    if (!tableData || !tableData.plans || (!tableData.features && !tableData.categories)) {
        return null;
    }

    const { plans: planKeys, categories } = tableData;
    const planLabelMap = (planDefinitions || []).reduce((accumulator, plan) => {
        accumulator[plan.id] = plan.label;
        return accumulator;
    }, {});

    const renderCellValue = (value) => {
        if (value === true) {
            return <CheckOutlined className="table-check" />;
        }

        if (value === false) {
            return <CloseOutlined className="table-cross" />;
        }

        if (value === null || value === undefined || value === "") {
            return <span className="table-cell-text">—</span>;
        }

        return <span className="table-cell-text">{value}</span>;
    };

    return (
        <div className="advanced-features-section">
            <div className="advanced-features-table-wrap">
                <table className="advanced-features-table">
                    <thead>
                        <tr>
                            <th className="feature-header">FEATURES</th>
                            {planKeys.map((planKey) => (
                                <th key={planKey}>{planLabelMap[planKey] || planKey}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map((category, catIndex) => (
                            <React.Fragment key={catIndex}>
                                <tr className="category-header-row">
                                    <td colSpan={planKeys.length + 1} className="category-header-cell">
                                        {category.title}
                                    </td>
                                </tr>
                                {Object.entries(category.features).map(([featureName, rowValues]) => (
                                    <tr key={featureName}>
                                        <td className="feature-name-cell">{featureName}</td>
                                        {planKeys.map((planKey) => (
                                            <td key={`${featureName}-${planKey}`}>{renderCellValue(rowValues[planKey])}</td>
                                        ))}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const PricingCard = ({ plan, isAnnual, discountPercentage, onViewAllFeatures }) => {
  const {
    id,
    name,
    price,
    credits,
    features,
    buttonText,
    isPopular,
    tiers
  } = plan;

  const [sliderValue, setSliderValue] = useState(0);

  // Determine current tier data for scalable plans (like Max)
  let currentTier = tiers ? tiers[sliderValue] : null;

  // Use base values or tiered values
  let basePrice = currentTier ? currentTier.price : price;
  let displayCredits = currentTier ? currentTier.credits : credits;
  let displayFeatures = currentTier ? currentTier.features : features;
  
  // Calculate display price based on billing cycle
  let displayPriceValue = basePrice;
  let billedYearlyAmount = 0;

  if (isAnnual && basePrice !== null) {
      if (currentTier && currentTier.annualPriceTotal) {
          // If explicit annual total is provided
          displayPriceValue = currentTier.annualPriceTotal / 12;
          billedYearlyAmount = currentTier.annualPriceTotal;
      } else {
          // Calculate standard discount
          displayPriceValue = basePrice * (1 - discountPercentage / 100);
          billedYearlyAmount = displayPriceValue * 12;
      }
  } else if (!isAnnual && basePrice !== null) {
      billedYearlyAmount = basePrice * 12; // Just for reference if needed, though usually not shown for monthly
  }

  // Format price string
  const formattedPrice = basePrice === null 
    ? "Let's Talk" 
    : `$${Math.round(displayPriceValue).toLocaleString()}`;

  const handleButtonClick = () => {
    // Analytics
    const urlParams = new URLSearchParams(window.location.search);
    const utm_source = urlParams.get('utm_source') || localStorage.getItem("utm_source") || "";
    const email = urlParams.get('email') || "";

    trackCta('plan_selection_click', {
      plan_name: name,
      plan_id: id,
      billing_cycle: isAnnual ? 'annual' : 'monthly',
      price: formattedPrice,
      multiplier: currentTier ? currentTier.multiplier : 1,
      section: 'pricing_plans',
    });

    // Navigation
    if (id === 'business') {
        window.open(`${ENVIRONMENT.CALENDLY_LINK}`, "_blank");
    } else if (id === 'free_trial') {
        window.open(`${ENVIRONMENT.FREE_TRIAL_LINK}`, "_blank");
    } else {
        // Build plan name based on tier multiplier
        let planName = id;
        if (currentTier && currentTier.multiplier > 1) {
            // e.g. business_ai -> business_2x_ai
            planName = id.replace('_ai', `_${currentTier.multiplier}x_ai`);
        }
        let queryParams = `plan=${isAnnual && planName != 'free_trial_ai' ? 'annual_' + planName : planName}&plan_type=subscription&utm_source=${utm_source}`;

        const redirectUrl = `/select-subscription?${queryParams}`;
        window.open(`${ENVIRONMENT.PORTAL_BASE_URL}/register?redirectURL=${encodeURIComponent(redirectUrl)}`, "_blank");
    }
  };

  const marks = tiers ? {
      0: '1x',
      1: '2x',
      2: '4x',
      3: '8x',
      4: '16x',
      5: '32x'
  } : {};

  return (
    <Col xs={24} sm={12} md={12} lg={6} xl={6} style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: '200px' }}>
      <Card 
        className={`pricing-card ${isPopular ? 'popular-card' : ''}`}
        bordered={true}
        hoverable
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        {isPopular && (
           <div className="popular-tag">
             <span className="manrope f-10 w-700 white lh-20">MOST POPULAR</span>
           </div>
        )}
        
        <h2 className="manrope f-20 w-700 lh-32 black-085 mb-8">{name}</h2>
        
        <div className="price-section mb-16">
            <span className="manrope f-32 w-700 black-085">
                {formattedPrice}
            </span>
            {price !== null && (
                <span className="manrope f-14 w-400 black-045">/month</span>
            )}
        </div>
        {isAnnual && price !== null && (
            <div className="billed-yearly mb-16 manrope f-12 black-045">
                Billed ${Math.round(billedYearlyAmount).toLocaleString()} yearly
            </div>
        )}

        <Button
            type={id === 'free_trial' ? 'default' : 'primary'}
            data-attr={`plan-card-${id}-cta`}
            className={`w-100 mb-8 manrope f-16 w-600 ${id === 'free_trial' ? 'free-trial-btn' : ''}`}
            onClick={handleButtonClick}
            style={{ height: '48px' }}
        >
            {buttonText}
        </Button>

        {displayCredits !== null && (
             <div className="text-center mb-24">
                <span className="manrope f-14 w-500 black-065">{displayCredits.toLocaleString()} Credits</span>
             </div>
        )}

        {tiers && (
            <div className="slider-container mb-24 px-12">
                <Slider 
                    min={0} 
                    max={tiers.length - 1} 
                    step={1} 
                    value={sliderValue} 
                    onChange={setSliderValue}
                    tooltipVisible={false}
                    ariaLabelForHandle={`${name} plan size multiplier`}
                    ariaValueTextFormatterForHandle={(value) => marks[value] || `${value + 1}x`}
                    trackStyle={{ backgroundColor: '#276dd7' }}
                    handleStyle={{ borderColor: '#276dd7', backgroundColor: '#276dd7' }}
                    marks={marks}
                />
            </div>
        )}

        <Divider className="mb-24 mt-0" />

        <div className="features-list">
            {displayFeatures && displayFeatures.map((feature, index) => (
                <div key={index} className="feature-item">
                    <CheckOutlined style={{ color: '#276dd7', fontSize: '14px' }} />
                    <span className="feature-value manrope f-14 black-065">{feature}</span>
                </div>
            ))}
        </div>

        <div className="view-all-features-wrapper">
            <Button
                type="link"
                data-attr={`plan-card-${id}-view-features`}
                className="view-all-features-btn manrope f-14 w-600"
                onClick={() => {
                    trackCta("view_features_click", {
                        section: "pricing_plans",
                        button: "view_all_features",
                        plan_id: id,
                        plan_name: name,
                    });
                    onViewAllFeatures();
                }}
            >
                View all features <span className="arrow-icon">→</span>
            </Button>
        </div>
      </Card>
    </Col>
  );
};

const EnterpriseCard = ({ enterpriseData }) => {
    return (
        <Row justify="center" className="mt-24" style={{ width: '100%' }}>
            <Col xs={24}>
                <Card className="pricing-card enterprise-card" bordered={true}>
                    <Row gutter={[24, 24]} align="middle">
                        <Col xs={0} lg={4} xl={5} />
                        
                        <Col xs={24} md={10} lg={8} xl={7} className="enterprise-title-col">
                            <div className="text-center-md text-left-lg">
                                <h2 className="manrope f-20 w-700 black-085 mb-16">{enterpriseData.title}</h2>
                                <Button
                                    type="primary"
                                    data-attr="plan-enterprise-cta"
                                    className="manrope f-16 w-600"
                                    onClick={() => {
                                        trackCta('schedule_demo_click', {
                                            section: 'pricing_enterprise_card',
                                            button: 'schedule_a_demo',
                                        });
                                        window.open(`${ENVIRONMENT.CALENDLY_LINK}`, "_blank");
                                    }}
                                    style={{ height: '48px', minWidth: '200px' }}
                                >
                                    {enterpriseData.buttonText}
                                </Button>
                            </div>
                        </Col>
                        
                        <Col xs={24} md={14} lg={8} xl={7}>
                            <div className="features-list-vertical">
                                {enterpriseData.features && enterpriseData.features.map((feature, index) => (
                                    <div key={index} className="feature-item">
                                        <CheckOutlined style={{ color: '#276dd7', fontSize: '14px' }} />
                                        <span className="feature-value manrope f-14 black-065">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </Col>

                        <Col xs={0} lg={4} xl={5} />
                    </Row>
                </Card>
            </Col>
        </Row>
    )
}

const Plans = () => {
  const [annual, setAnnual] = useState(false);
    const advancedFeaturesRef = useRef(null);

    const { plans, discountPercentage, enterprise, advancedFeaturesTable } = pricingData;
    const tablePlanDefinitions = [
        ...plans.map((plan) => ({ id: plan.id, label: plan.name })),
        ...(enterprise ? [{ id: "enterprise", label: "Enterprise" }] : [])
    ];

    const handleViewAllFeatures = () => {
        window.requestAnimationFrame(() => {
            if (advancedFeaturesRef.current) {
                advancedFeaturesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        });
    };

  return (
    <div className="pricing-plans-wrapper">
      <div className="pricing-plans-container">
        <div className="text-center mb-40">
             <h1 className="manrope f-40 w-700 mb-16 black-085">Pricing</h1>
             <Paragraph className="manrope f-16 w-500 black-045 mb-24">
                Find the perfect plan to create accurate, brand-specific images to showcase your products.
             </Paragraph>
             
             <div className="subscription-toggle-wrapper justify-center flex items-center">
                <span className={`manrope f-14 ${!annual ? 'w-700 black-085' : 'w-400 black-045'} mr-12`}>Monthly</span>
                <Switch
                    checked={annual}
                    onChange={(checked) => {
                        trackCta("billing_cycle_toggle", {
                            section: "pricing_plans",
                            button: "billing_cycle_switch",
                            billing_cycle: checked ? "annual" : "monthly",
                        });
                        setAnnual(checked);
                    }}
                    aria-label={`Billing frequency: ${annual ? "annually" : "monthly"}`}
                />
                <span className={`manrope f-14 ${annual ? 'w-700 black-085' : 'w-400 black-045'} ml-12`}>Annually</span>
                {discountPercentage > 0 && (
                    <Tag className="ml-12 border-round" style={{ backgroundColor: '#389e0d', color: '#fff', border: 'none' }}>
                        Save {discountPercentage}%
                    </Tag>
                )}
             </div>
        </div>

        <Row gutter={[16, 16]} justify="center" style={{ display: 'flex', flexWrap: 'wrap' }}>
            {plans.filter(p => p.id !== 'business').map(plan => (
                <PricingCard 
                    key={plan.id} 
                    plan={plan} 
                    isAnnual={annual} 
                    discountPercentage={discountPercentage} 
                                        onViewAllFeatures={handleViewAllFeatures}
                />
            ))}
        </Row>

        {enterprise && <EnterpriseCard enterpriseData={enterprise} />}

        <div ref={advancedFeaturesRef} className="features-table-block">
            <div className="features-table-heading text-center mb-16">
                <h2 className="manrope f-32 w-700 black-085 mb-8">Compare Advanced Features</h2>
                <Paragraph className="manrope f-16 w-500 black-045 mb-2">
                    Clear side-by-side comparison to find the right plan for your team.
                </Paragraph>
            </div>
            <DynamicFeaturesTable tableData={advancedFeaturesTable} planDefinitions={tablePlanDefinitions} />
        </div>

        <div className="text-center mt-40">
            <Paragraph className="manrope f-14 black-045">
                All subscriptions are annual contracts which will automatically renew each year. You may cancel at any time before the renewal date.{" "}
                <a
                    href="#faqs"
                    data-attr="plans-see-faqs"
                    className="blue faq-link"
                    onClick={() => trackCta("view_faqs_click", { section: "pricing_plans", button: "see_faqs" })}
                >See FAQs</a>
            </Paragraph>
        </div>
      </div>
    </div>
  );
};

export default Plans;
